import { getFirestore, getCloudStorage } from '../../system/firebase';

import {
    SNACK_BAR_MESSAGE_DELETE_VIDEO,
    SNACK_BAR_MESSAGE_OPERATION_FAILED,
    SNACK_BAR_MESSAGE_SAVE,
    VIDEO_OVERVIEW_MODE_FROM_CAMERA,
    SNACK_BAR_MESSAGE_SAVED_TO_DRAFT,
    SNACK_BAR_MESSAGE_SAVED_TO_PUBLIC,
    UPLOADED_VIDEO_STATUS_PROCESSING,
    UPLOADED_VIDEO_STATUS_APPROVED,
} from '../constants';
import { mapCreatableLovOptionsToValueList, getNewLovOptions /*, isNewLovOption*/, mapCreatableLovOptionToValue, getNewLovOption } from '../lovHelper';
import { deleteEmptyFields } from '../dtoHelper';
import { scrubUrl } from '../linkHelper';

import { format as formatDate } from 'date-fns';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _map from 'lodash/map';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';

export const saveProfileAboutMe = async ({
    userId,
    firstName,
    middleName,
    lastName,
    gender,
    race,
    country,
    state,
    city,
    cityLocation,
    newCity,
    employmentType,
    yearsOfExperience,
    isExactRate,
    rateMin,
    rateMax,
    currency,
    currencySymbol,
    payFrequency,
    occupation,
    skillset,
    education,
    otherEducation,
    aboutMe,
    willingRelocate,
    closeDialog,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    // const db = firebase.firestore();
    const db = getFirestore();
    const batch = db.batch();

    try {
        //------------------------------------------------------------------------------------
        // Update profile doc
        //------------------------------------------------------------------------------------
        const profileRef = db.collection('profiles').doc(userId);
        const dto = {
            // // name and location fields are now part of the 'users' doc
            // name: name || null,
            // country: country || null,
            // state: state || null,
            // city: _get(city, 'name', null),
            // cityLocation: cityLocation || null,
            gender: mapCreatableLovOptionToValue(gender),
            race,
            employmentType: mapCreatableLovOptionsToValueList({ options: employmentType }),
            isExactRate,
            rateMin,
            rateMax,
            currency,
            currencySymbol,
            payFrequency,
            yearsOfExperience,
            occupation: mapCreatableLovOptionsToValueList({ options: occupation }),
            skillset: mapCreatableLovOptionsToValueList({ options: skillset }),
            education: education || null,
            otherEducation: mapCreatableLovOptionsToValueList({ options: otherEducation }),
            aboutMe,
            willingRelocate,
        };
        batch.set(
            profileRef,
            {
                ...deleteEmptyFields(dto),
                updateDt: new Date(),
            },
            { merge: true },
        );

        //------------------------------------------------------------------------------------
        // Update user doc
        //------------------------------------------------------------------------------------
        const userRef = db.collection('users').doc(userId);
        const userDto = {
            firstName: firstName || null,
            middleName: middleName || null,
            lastName: lastName || null,
            country: country || null,
            state: state || null,
            city: city || null, //_get(city, 'name', null),
            cityLocation: cityLocation || null,
        };
        batch.set(
            userRef,
            {
                ...deleteEmptyFields(userDto),
                updateDt: new Date(),
            },
            { merge: true },
        );

        //------------------------------------------------------------------------------------
        // Add new LOV options (if any)
        //------------------------------------------------------------------------------------
        const newLovOptionRef = db.collection('newLovOptions').doc();
        const newLovOptionsDto = {};

        const newGenderOption = getNewLovOption(gender);
        if (newGenderOption) {
            _set(newLovOptionsDto, 'newGenderOptions', [newGenderOption]);
        }

        const newOccupationOptions = getNewLovOptions(occupation);
        if (newOccupationOptions.length > 0) {
            _set(newLovOptionsDto, 'newOccupationOptions', newOccupationOptions);
        }

        const newSkillsetOptions = getNewLovOptions(skillset);
        if (newSkillsetOptions.length > 0) {
            _set(newLovOptionsDto, 'newSkillsetOptions', newSkillsetOptions);
        }

        const newOtherEducationOptions = getNewLovOptions(otherEducation);
        if (newOtherEducationOptions.length > 0) {
            _set(newLovOptionsDto, 'newOtherEducationOptions', newOtherEducationOptions);
        }

        if (newCity) {
            _set(newLovOptionsDto, 'newCityOption', newCity);
            _set(newLovOptionsDto, 'newCityOptionState', state || 'Unknown State');
            _set(newLovOptionsDto, 'newCityOptionCountry', country || 'Unknown Country');
        }

        if (!_isEmpty(newLovOptionsDto)) {
            batch.set(newLovOptionRef, {
                ...newLovOptionsDto,
                createDt: new Date(),
                createBy: userId,
            });
        }

        //------------------------------------------------------------------------------------
        // commit changes
        //------------------------------------------------------------------------------------
        await batch.commit();

        // console.log('Transaction successfully committed!');
        closeDialog();
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.error('Save profile about me transaction failed: ', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const saveProfileResume = async ({ userId, resumeList, closeDialog, setProgress, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const storage = getCloudStorage();
    const attachmentList = [];
    const promises = [];
    const updateDt = new Date();

    //------------------------------------------------------------------------------------------------
    // Iterate through the resumeList, and upload the selected files (if any).
    // It also set up the 'attachment' objects.
    //
    // If file is being uploaded, the 'url' will be set upon completion.
    //
    // If the file is marked for deletion, set 'attachment' to null and delete the file (TO DO).
    //
    // If no selected file to be uploaded and not marked for deletion, carry over the 'attachment'
    //------------------------------------------------------------------------------------------------
    _forEach(resumeList, (resume, idx) => {
        const file = _get(resume, 'attachment.selectedFile');
        if (file) {
            const name = _get(file, 'name');
            const timestampStr = formatDate(new Date(), 'yyyyMMddHHmmss_T');
            const storagePath = `users/${userId}/resumes/${timestampStr}`;
            const uploadTask = storage.ref(`${storagePath}/${name}`).put(file, { customMetadata: { userId, key: timestampStr, originFilename: name, isResume: true } });

            //TODO if replacing file, delete the previous file from storage.

            // setIsUploading(true);
            // create a promise for each file upload and put in array so we can use Promise.all later
            promises.push(
                new Promise((resolve, reject) => {
                    uploadTask.on(
                        'state_changed',
                        snapshot => {
                            // progress function ...
                            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                            setProgress(idx, progress);
                        },
                        error => {
                            // Error function ...
                            console.error('Resume file upload failed', error);
                        },
                        async () => {
                            // complete function ...
                            try {
                                const url = await storage.ref(storagePath).child(name).getDownloadURL();

                                // build a new attachment object
                                _set(attachmentList, `[${idx}]`, { url, title: name, size: _get(file, 'size'), type: _get(file, 'type'), key: timestampStr });

                                resolve();
                                // onAttachmentUploadComplete(file, url);
                            } catch (error) {
                                console.error('Failed to obtain resume file URL:', error);
                                reject(error);
                            }
                        },
                    );
                }),
            );
        } else {
            // if no selected file, check if user is deleting the previously uploaded file.
            const isDelete = _get(resume, 'attachment.isDelete');
            if (isDelete) {
                // delete resume
                _set(attachmentList, `[${idx}]`, null);

                //TODO delete the file from storage
            } else {
                // use the original attachment object
                _set(attachmentList, `[${idx}]`, _get(resume, 'attachment'));
            }
        }
    });

    try {
        // Wait for all file upload complete.
        await Promise.all(promises);

        // All files uploaded. Saving the profile data now.
        const dto = {
            resumeList: _map(resumeList, (resume, idx) => {
                return {
                    isActive: _get(resume, 'isActive'),
                    title: _get(resume, 'title'),
                    attachment: _get(attachmentList, `[${idx}]`, null),
                    updateDt: _get(resume, 'isUpdated') ? updateDt : _get(resume, 'updateDt'),
                };
            }),
        };
        const profileRef = db.collection('profiles').doc(userId);

        await db.runTransaction(transaction => {
            // This code may get re-run multiple times if there are conflicts.
            return transaction.get(profileRef).then(function (/* doc */) {
                // if (!doc.exists) {
                //     throw new Error('Profile document does not exist!');
                // }
                transaction.update(profileRef, {
                    ...dto,
                    updateDt,
                });
            });
        });

        // console.log('Transaction successfully committed!');
        closeDialog();
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
        return true;
    } catch (error) {
        // console.error(error.code);
        console.error('Save profile resume transaction failed: ', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const saveProfileType = async ({ userId, isPublic, dataDidSave, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();

    const profileRef = db.collection('profiles').doc(userId);

    try {
        await db.runTransaction(transaction => {
            // This code may get re-run multiple times if there are conflicts.
            return transaction.get(profileRef).then(function (doc) {
                if (!doc.exists) {
                    throw new Error('Profile document does not exist!');
                }

                const dto = {
                    isPublic: isPublic ? true : false,
                };

                transaction.update(profileRef, {
                    ...deleteEmptyFields(dto),
                    updateDt: new Date(),
                });
            });
        });

        // console.log('Transaction successfully committed!');
        dataDidSave && dataDidSave();
        setSnackbarMessage(isPublic ? SNACK_BAR_MESSAGE_SAVED_TO_PUBLIC : SNACK_BAR_MESSAGE_SAVED_TO_DRAFT);

        return true;
        ////
    } catch (error) {
        console.error('Save profile type transaction failed: ', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const changeProfileDraft = async ({ userId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();

    const profileRef = db.collection('profiles').doc(userId);

    try {
        await db.runTransaction(transaction => {
            return transaction.get(profileRef).then(function (doc) {
                if (!doc.exists) throw new Error('Profile document does not exist!');

                const dto = { isPublic: false };

                transaction.update(profileRef, {
                    ...deleteEmptyFields(dto),
                    updateDt: new Date(),
                });
            });
        });
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVED_TO_DRAFT);
        return true;
        ////
    } catch (error) {
        console.error('Save profile type transaction failed: ', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const saveProfileSocialMedia = async ({
    userId,
    facebookUrl,
    linkedInUrl,
    youTubeUrl,
    instagramUrl,
    twitterUrl,
    websiteUrl,
    tikTokUrl,
    gitHubUrl,
    setSnackbarMessage,
    setSnackbarErrorMessage,
}) => {
    facebookUrl = scrubUrl(facebookUrl);
    linkedInUrl = scrubUrl(linkedInUrl);
    youTubeUrl = scrubUrl(youTubeUrl);
    twitterUrl = scrubUrl(twitterUrl);
    instagramUrl = scrubUrl(instagramUrl);
    websiteUrl = scrubUrl(websiteUrl);
    tikTokUrl = scrubUrl(tikTokUrl);
    gitHubUrl = scrubUrl(gitHubUrl);

    const db = getFirestore();

    const profileRef = db.collection('profiles').doc(userId);

    const dto = deleteEmptyFields({
        facebookUrl,
        linkedInUrl,
        youTubeUrl,
        instagramUrl,
        twitterUrl,
        websiteUrl,
        tikTokUrl,
        gitHubUrl,
    });

    try {
        await db.runTransaction(transaction => {
            return transaction.get(profileRef).then(function (doc) {
                if (!doc.exists) {
                    throw new Error('Document does not exist!');
                }

                transaction.set(profileRef, dto, { merge: true });
            });
        });

        // console.log('Transaction successfully committed!');
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
        return true;
        ////
    } catch (error) {
        console.error('Save Profile social media transaction failed: ', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const sendVideoOverview = ({ mode, video, duration = 0, userId, setProgress, setSnackbarMessage, setSnackbarErrorMessage, cleanUpDialog }) => {
    // console.log('%c@@@@ sendVideoOverview START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ sendVideoOverview START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ sendVideoOverview START', 'background: #222; color: #bada55');
    // console.log('%c@@@@ sendVideoOverview video=', 'background: #222; color: #bada55', video);
    // console.log('%c@@@@ sendVideoOverview userId=', 'background: #222; color: #bada55', userId);
    // console.log('%c@@@@ sendVideoOverview cleanUpDialog=', 'background: #222; color: #bada55', cleanUpDialog);

    if (video && userId) {
        // console.log('%c@@@@ sendVideoOverview video and userId exists, start sending', 'background: #222; color: #bada55');

        const RECORDED_VIDEO_FILE_NAME = 'overview.webm';

        const storage = getCloudStorage();
        const name = mode === VIDEO_OVERVIEW_MODE_FROM_CAMERA ? RECORDED_VIDEO_FILE_NAME : _get(video, 'name'); // assume if not 'record' mode, it is a file selected from local file system
        const timestampStr = formatDate(new Date(), 'yyyyMMddHHmmss_T');
        const storagePath = `users/${userId}/videoOverview/${timestampStr}`;
        const uploadTask = storage.ref(`${storagePath}/${name}`).put(video, { customMetadata: { userId, key: timestampStr, originFilename: name, isJsVideo: 'Y' } });
        // const uploadTask = storage.ref(`users/${userId}/videoOverview/video.webm`).put(video);

        uploadTask.on(
            'state_changed',
            snapshot => {
                // progress function ...
                if (setProgress) {
                    const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                    setProgress(progress);
                }
            },
            error => {
                // Error function ...
                console.error('File upload failed', error);
            },
            async () => {
                // complete function ...
                try {
                    console.log('%c@@@@ sendVideoOverview COMPLETE', 'background: #222; color: #bada55');

                    // const url = await storage.ref(`users/${userId}/videoOverview`).child('video.webm').getDownloadURL();
                    const url = await storage.ref(storagePath).child(name).getDownloadURL();
                    const videoOverviewSize = _get(video, 'size');
                    const videoOverviewType = _get(video, 'type');

                    const db = getFirestore();

                    const profileRef = db.collection('profiles').doc(userId);
                    const uploadedVideosRef = db.collection('uploadedVideos').doc();

                    return db
                        .runTransaction(transaction => {
                            return transaction.get(profileRef).then(function (doc) {
                                if (!doc.exists) {
                                    throw new Error('Document does not exist!');
                                }

                                transaction.update(profileRef, { uploadedVideoStatus: UPLOADED_VIDEO_STATUS_PROCESSING });
                                transaction.set(uploadedVideosRef, {
                                    createDt: new Date(),
                                    status: UPLOADED_VIDEO_STATUS_PROCESSING,
                                    userId,
                                    videoOverviewUrl: url,
                                    videoOverviewName: name,
                                    videoOverviewKey: timestampStr,
                                    videoOverviewSize,
                                    videoOverviewType,
                                    videoOverviewDuration: duration,
                                });

                                cleanUpDialog();

                                console.log('%c@@@@ sendVideoOverview COMPLETE url=', 'background: #222; color: #bada55', url);
                            });
                        })
                        .then(function () {
                            // console.log('Transaction successfully committed!');
                            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
                        })
                        .catch(function (error) {
                            console.error('Save Profile Video Overview transaction failed: ', error);
                            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                        });
                } catch (error) {
                    console.error('Failed to obtain file URL:', error);
                }
            },
        );
    } else {
        console.log('%c@@@@ sendVideo file and userId does not exist, do nothing', 'background: #222; color: #bada55');
    }
};

export const deleteVideoOverview = async ({ userId, key, name, setSnackbarMessage, setSnackbarErrorMessage, cleanUpDialog }) => {
    console.log('%c@@@@ deleteVideoOverview START', 'background: #222; color: #bada55');
    console.log('%c@@@@ deleteVideoOverview START', 'background: #222; color: #bada55');
    console.log('%c@@@@ deleteVideoOverview START', 'background: #222; color: #bada55');
    console.log('%c@@@@ deleteVideoOverview userId=', 'background: #222; color: #bada55', userId);
    console.log('%c@@@@ deleteVideoOverview cleanUpDialog=', 'background: #222; color: #bada55', cleanUpDialog);

    if (userId) {
        console.log('%c@@@@ deleteVideoOverview userId exists, start deleting', 'background: #222; color: #bada55');

        const storage = getCloudStorage();
        try {
            await storage.ref(`users/${userId}/videoOverview/${key}/${name}`).delete();
            console.log('%c@@@@ deleteVideoOverview COMPLETE', 'background: #222; color: #bada55');
            ////
        } catch (error) {
            console.error('Failed to delete file from storage', error);
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
            return false;
        }

        const db = getFirestore();
        const profileRef = db.collection('profiles').doc(userId);

        try {
            await db.runTransaction(async transaction => {
                const doc = await transaction.get(profileRef);
                if (!doc.exists) {
                    throw new Error('Document does not exist!');
                }

                transaction.update(profileRef, {
                    videoOverviewUrl: null,
                    videoOverviewName: null,
                    videoOverviewKey: null,
                    videoOverviewSize: 0,
                    videoOverviewType: null,
                    videoOverviewDuration: 0,
                    uploadedVideoStats: null, // for removing stats old of bad video.
                    uploadedVideoStatus: null, // for reseting the video status
                });

                cleanUpDialog();

                console.log('%c@@@@ deleteVideoOverview COMPLETE', 'background: #222; color: #bada55');
            });

            // console.log('Transaction successfully committed!');
            setSnackbarMessage(SNACK_BAR_MESSAGE_DELETE_VIDEO);
            return true;
            ////
        } catch (error) {
            console.error('Delete Profile Video Overview transaction failed: ', error);
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
            return false;
        }
    } else {
        console.log('%c@@@@ sendVideo file and userId does not exist, do nothing', 'background: #222; color: #bada55');
    }
};

export const resetVideoOverviewStatus = ({ userId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();

    const profileRef = db.collection('profiles').doc(userId);

    return db
        .runTransaction(transaction => {
            // This code may get re-run multiple times if there are conflicts.
            return transaction.get(profileRef).then(function (doc) {
                if (!doc.exists) {
                    throw new Error('Profile document does not exist!');
                }

                const dto = {
                    uploadedVideoStats: null, // for removing stats old of bad video.
                    uploadedVideoStatus: UPLOADED_VIDEO_STATUS_APPROVED, // for resetting the video status
                };

                transaction.update(profileRef, {
                    ...deleteEmptyFields(dto),
                    updateDt: new Date(),
                });
            });
        })
        .then(function () {
            // console.log('Transaction successfully committed!');
            // dataDidSave();
            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
        })
        .catch(function (error) {
            console.error('Save profile type transaction failed: ', error);
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        });
};

export const saveProfilePicture = ({ file, userId, setProgress, setIsUploading, closeDialog, setSnackbarMessage, setSnackbarErrorMessage /*, onAttachmentUploadComplete */ }) => {
    const db = getFirestore();
    const storage = getCloudStorage();
    const name = _get(file, 'name');
    const timestampStr = formatDate(new Date(), 'yyyyMMddHHmmss_T');
    const storagePath = `users/${userId}/profilePicture/${timestampStr}`;
    const uploadTask = storage.ref(`${storagePath}/${name}`).put(file, { customMetadata: { userId, key: timestampStr, originFilename: name, isJsProfilePic: true } });
    setIsUploading(true);
    uploadTask.on(
        'state_changed',
        snapshot => {
            // progress function ...
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(progress);
        },
        error => {
            // Error function ...
            console.error('File upload failed', error);
        },
        async () => {
            // complete function ...
            try {
                const url = await storage.ref(storagePath).child(name).getDownloadURL();

                // All files uploaded. Saving the profile data now.
                const dto = {
                    profilePicture: { url, title: name, size: _get(file, 'size'), type: _get(file, 'type'), key: timestampStr },
                };
                const profileRef = db.collection('profiles').doc(userId);
                return db
                    .runTransaction(transaction => {
                        // This code may get re-run multiple times if there are conflicts.
                        return transaction.get(profileRef).then(function (doc) {
                            if (!doc.exists) {
                                throw new Error('Profile document does not exist!');
                            }
                            transaction.update(profileRef, {
                                ...dto,
                                // updateDt,
                            });
                        });
                    })
                    .then(function () {
                        // console.log('Transaction successfully committed!');
                        closeDialog();
                        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
                    })
                    .catch(function (error) {
                        console.error('Save profile picture info transaction failed: ', error);
                        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
                    });
                // onAttachmentUploadComplete(file, url);
            } catch (error) {
                console.error('Failed to obtain file URL:', error);
            }
        },
    );
};
